/**
 * Value-to-height scaling for `TimelineScrubber`, plus the pixel constants
 * that describe its fixed layout (bar band, timeline axis, tooltip gap).
 */

export type TimelineScrubberScale = 'linear' | 'sqrt' | 'log';

/** Default shared reference scale — see `TimelineScrubberProps.baselineMax`. */
export const DEFAULT_BASELINE_MAX = 10;

// A true 0% height renders nothing at all, which reads as "missing" rather
// than "zero" next to 24 other bars. A fixed 2px pale line keeps every hour
// visible — "this bucket exists, but has no results" — without looking like
// a real (if tiny) value: it's shorter and fainter than any nonzero bar can
// render at this baseline (a "1" is at least 4px, see NONZERO_MIN_OPACITY).
export const ZERO_MARK_HEIGHT_PX = 2;
export const ZERO_MARK_OPACITY = 0.18;

// Nonzero opacity is `Math.max(NONZERO_MIN_OPACITY, heightRatio)`, so even a
// lone "1" result (heightRatio 0.1 at the default baseline) still reads as a
// visible, if pale, green bar rather than fading toward the zero-line's
// opacity — while staying clearly less prominent than a full 10+ bar.
export const NONZERO_MIN_OPACITY = 0.4;

// Must match the `h-10` band each bar renders in. Bars are centered within
// this band, so half of it — fixed, regardless of which bucket is hovered —
// is the timeline's horizontal axis: every bar's vertical center sits at
// exactly this distance from the track's bottom edge, no matter its height.
export const BAR_BAND_HEIGHT_PX = 40;
export const TIMELINE_CENTER_FROM_BOTTOM_PX = BAR_BAND_HEIGHT_PX / 2;

// The vertical space the tooltip and its guide line float in, measured from
// the top of the component down to the bottom of the bar band. The tooltip
// is pinned to this zone's top edge and only the guide line's length varies
// with bar height, so this is what sets the clearance above a full-height
// bar: 84 less the 40px band leaves ~44px, of which the tooltip takes ~33px.
export const REVEAL_ZONE_HEIGHT_PX = 84;

// Distance from the reveal zone's TOP edge down to the timeline axis.
// Anything that must line up with the axis measures from the top rather than
// the bottom, so that adding content *below* the track (e.g. the selected
// hour's readout) can't drag it out of alignment.
export const AXIS_FROM_TOP_PX = REVEAL_ZONE_HEIGHT_PX - TIMELINE_CENTER_FROM_BOTTOM_PX;

/**
 * Maps `value` against the fixed `baselineMax` — not the dataset's own max
 * — to a `[0, 1]` height ratio. `0` always reaches `0`; `baselineMax` (and
 * anything beyond it) always clamps to `1`, regardless of scale.
 */
export function normalizeHeight(
  value: number,
  baselineMax: number,
  scale: TimelineScrubberScale,
): number {
  if (baselineMax <= 0 || value <= 0) return 0;
  const ratio = Math.min(1, value / baselineMax);
  switch (scale) {
    case 'sqrt':
      return Math.min(1, Math.sqrt(ratio));
    case 'log':
      // log1p is defined at 0 and grows sub-linearly. Derived from the
      // already-clamped ratio (not the raw value) so it can't exceed 1.
      return Math.min(1, Math.log1p(ratio) / Math.log1p(1));
    default:
      return ratio;
  }
}

/** Pixel height a bucket's bar actually renders at (real value or the zero-mark). */
export function barHeightPx(heightRatio: number): number {
  return heightRatio > 0 ? heightRatio * BAR_BAND_HEIGHT_PX : ZERO_MARK_HEIGHT_PX;
}
